import { FC, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, OpenInNew } from '@mui/icons-material';
import {
  Box,
  ButtonBase,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  selectClasses,
} from '@mui/material';

import { ContentThumbnail } from '../../../components/ContentThumbnail';
import {
  CourseSection,
  Enrollment,
  displayCourseSectionName,
  isAdminRole,
  isInstructorRole,
  isProduct,
  isStudentRole,
} from '../../../models';
import { useMainLayoutContext } from '../Context/useMainLayoutContext';

interface Props {
  courseSections: CourseSection[] | undefined;
  selectedCourseSectionId: number | null;
  isLoading: boolean;
  onChangeSelectedCourse: (id: number) => void;
  onUnlockCourse: (enrollment: Enrollment) => void;
}

export const CourseSelector: FC<Props> = ({
  courseSections,
  selectedCourseSectionId,
  isLoading,
  onChangeSelectedCourse,
  onUnlockCourse,
}) => {
  const labelId = useId();
  const { t } = useTranslation();
  const isAdmin = isAdminRole();

  const { isDrawerCollapsed, toggleDrawer } = useMainLayoutContext();

  const [defaultMenuOpen, setDefaultMenuOpen] = useState(false);

  const selectedCourse =
    courseSections?.find((cs) => cs.id === selectedCourseSectionId) ?? courseSections?.[0];
  const selectedEnrollment = selectedCourse?.enrollments[0];

  const courseName = displayCourseSectionName(selectedCourse);

  const mainProduct = selectedCourse?.products?.[0];
  const mainContent = selectedCourse?.contents?.[0];
  const item = mainProduct ?? mainContent;
  const itemHref = item
    ? isProduct(item)
      ? `/products/content/${item.id}`
      : `/contents/${item.id}`
    : '';

  const imgDimensions = isDrawerCollapsed ? 40 : 140;

  const hasMultipleCourses = !!courseSections && courseSections.length > 1;

  const currentTerm = selectedCourse?.term;
  const termStart = currentTerm?.startAt;
  const termEnd = currentTerm?.endAt;

  const calculateTermProgress = (): number => {
    if (!termStart || !termEnd) {
      return 0;
    }

    const now = new Date();
    const start = new Date(termStart);
    const end = new Date(termEnd);

    if (now < start) {
      return 0;
    }

    if (now > end) {
      return 100;
    }

    const totalDuration = end.getTime() - start.getTime();
    const elapsed = now.getTime() - start.getTime();

    return Math.round((elapsed / totalDuration) * 100);
  };

  const termProgress = calculateTermProgress();

  const handleChangeCourseSection = (courseSectionId: number) => {
    const enrollment = courseSections?.find((cs) => cs.id === courseSectionId)?.enrollments?.[0];
    const isInstructor = isInstructorRole(enrollment);

    const canAccessInactiveEnrollment = isInstructor || isAdmin;

    if (enrollment?.isActive || canAccessInactiveEnrollment) {
      onChangeSelectedCourse(courseSectionId);
      return;
    }

    if (enrollment) {
      onUnlockCourse(enrollment);
    }
  };

  if (!isLoading && (!courseSections || selectedCourseSectionId === null)) return null;

  const onboardingProgress =
    ((selectedCourse?.percentOnboard ?? 0) + (selectedCourse?.syllabusPercentOnboard ?? 0)) / 2;

  return (
    <>
      {!isDrawerCollapsed && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            pb: 3,
          }}
        >
          {isLoading && <Skeleton variant="text" width="100%" sx={{ my: 1, py: 1 }} />}

          {!isLoading && !hasMultipleCourses && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                component="p"
                fontWeight="fontWeightMedium"
                title={courseName}
                noWrap
                sx={{ m: 0 }}
              >
                {courseName}
              </Typography>

              {!selectedEnrollment?.isActive && isStudentRole(selectedEnrollment) && (
                <Lock fontSize="small" />
              )}
            </Stack>
          )}

          {!isLoading && hasMultipleCourses && (
            <FormControl fullWidth size="small">
              <InputLabel id={labelId}>{t('selectCourse')}</InputLabel>
              <Select<number>
                labelId={labelId}
                label={t('selectCourse')}
                value={selectedCourseSectionId ?? ''}
                defaultOpen={defaultMenuOpen}
                MenuProps={{
                  slotProps: {
                    paper: {
                      sx: { width: 232 },
                    },
                  },
                }}
                sx={{
                  backgroundColor: 'transparent',

                  [`& .${selectClasses.select}`]: {
                    display: 'flex',
                  },
                }}
                onChange={(e) => handleChangeCourseSection(e.target.value)}
              >
                {courseSections.map((x) => {
                  const enrollment = courseSections?.find((cs) => cs.id === x.id)?.enrollments?.[0];
                  return (
                    <MenuItem key={x.id} value={x.id} sx={{ gap: 1 }}>
                      {!enrollment?.isActive && isStudentRole(enrollment) && (
                        <ListItemIcon sx={{ ml: 'auto', minWidth: '0 !important' }}>
                          <Lock fontSize="small" />
                        </ListItemIcon>
                      )}
                      <ListItemText
                        primary={displayCourseSectionName(x)}
                        secondary={x.term?.name}
                      />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}
        </Box>
      )}

      {item && !isLoading && (
        <Box
          key={selectedCourseSectionId}
          sx={{
            position: 'relative',
            mx: 'auto',
            mb: isDrawerCollapsed ? 0 : 3,
          }}
        >
          {isDrawerCollapsed ? (
            <Tooltip
              title={hasMultipleCourses ? t('selectCourseSection') : `${t('open')}: ${item.name}`}
              placement="right"
              arrow
            >
              {hasMultipleCourses || !itemHref ? (
                <ButtonBase
                  sx={{ borderRadius: '4px' }}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleDrawer(true);
                    setDefaultMenuOpen(true);
                  }}
                >
                  <ContentThumbnail item={item} />
                </ButtonBase>
              ) : (
                <ButtonBase href={itemHref!} sx={{ borderRadius: '4px' }}>
                  <ContentThumbnail item={item} />
                </ButtonBase>
              )}
            </Tooltip>
          ) : (
            <ContentThumbnail item={item} size={140} />
          )}

          {!isDrawerCollapsed && (
            <IconButton
              href={itemHref}
              variant="filled"
              color="primary"
              aria-label={`${t('open')}: ${item.name}`}
              sx={{
                position: 'absolute',
                bottom: 0,
                right: '50%',
                transform: 'translateX(50%) translateY(50%)',
              }}
            >
              <OpenInNew />
            </IconButton>
          )}
        </Box>
      )}

      {selectedCourse?.percentOnboard !== 100 &&
        !isLoading &&
        !isDrawerCollapsed &&
        currentTerm && (
          <Stack sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1 }}>Onboarding:</Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1 }}>
              <LinearProgress
                variant="determinate"
                value={onboardingProgress}
                aria-label={t('onboardingProgress', 'Onboarding Progress')}
                sx={{ width: '100%' }}
              />
              <Typography variant="body2" textAlign="end" sx={{ minWidth: 35 }} aria-hidden>
                {`${onboardingProgress ?? 0}%`}
              </Typography>
            </Box>
          </Stack>
        )}

      {selectedCourse?.percentOnboard === 100 &&
        !isLoading &&
        !isDrawerCollapsed &&
        currentTerm && (
          <Stack sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1 }}>
              <LinearProgress
                variant="determinate"
                value={termProgress}
                aria-label={t('termProgress', 'Term Progress')}
                sx={{ width: '100%' }}
              />
              <Typography variant="body2" textAlign="end" sx={{ minWidth: 35 }} aria-hidden>
                {`${termProgress}%`}
              </Typography>
            </Box>
          </Stack>
        )}

      {isLoading && (
        <Stack alignItems="center" gap={1}>
          <Skeleton
            variant="rounded"
            sx={{
              width: imgDimensions,
              height: imgDimensions,
              mx: 'auto',
              mb: isDrawerCollapsed ? 0 : 3,
            }}
          />

          {!isDrawerCollapsed && <Skeleton variant="text" width={200} sx={{ my: 1 }} />}
        </Stack>
      )}
    </>
  );
};
